import { signInWindow } from '../oAuthWindow';
import { inferProfileUrl, hasRtkpCookie, isUsingKc } from '../utils';
import { urlFromClientData } from './helpers';
import { find, get } from 'lodash';

const state = {
    accessToken: null,
    isInRetryMode: false,
    queuedRequest: [],
    headRequest: undefined,
    loggedUser: undefined,
    loggedUserCommunication: undefined,
    patients: [],
    loading: false,
    locale: null,
    logoutConfirmationModal: false,
};

const getters = {
    getAccessToken(state) {
        return state.accessToken;
    },

    getIsInRetryMode(state) {
        return state.isInRetryMode;
    },

    getHeadRequest(state) {
        return state.headRequest;
    },

    getLoggedUser(state) {
        return state.loggedUser;
    },

    getLoggedUserCommunication(state) {
        return state.loggedUserCommunication;
    },

    getProfileUrl() {
        return inferProfileUrl();
    },

    getPatients(state) {
        return state.patients;
    },

    getOwnerPatient(state) {
        const owner = find(state.patients, { associationType: 'owner' });
        return owner || {};
    },

    getIsOwnerRegisterComplete(state, getters) {
        if (!getters.getOwnerPatient) return false;
        return !!(get(getters.getOwnerPatient, 'firstName') && get(getters.getOwnerPatient, 'lastName'));
    },

    getLoading(state) {
        return state.loading;
    },

    getLocale(state) {
        return state.locale;
    },
    getLogoutConfirmationModal(state) {
        return state.logoutConfirmationModal;
    },
};

const mutations = {
    SET_ACCESS_TOKEN(state, token) {
        state.accessToken = token;
    },

    CLEAR_ACCESS_TOKEN(state) {
        state.accessToken = null;
    },

    SET_IS_IN_RETRY_MODE(state, value) {
        state.isInRetryMode = value;
    },

    ENQUEUE_REQUEST(state, req) {
        state.queuedRequest.push(req);
    },

    DEQUEUE_REQUEST(state) {
        state.headRequest = state.queuedRequest.shift();
    },

    SET_LOGGED_USER(state, user) {
        state.loggedUser = user;
    },

    SET_LOGGED_USER_COMMUNICATION(state, payload) {
        state.loggedUserCommunication = payload;
    },

    SET_PATIENTS(state, patients) {
        state.patients = patients;
    },

    SET_LOADING(state, payload) {
        state.loading = payload;
    },

    SET_LOCALE_FOR_PROFILE(state, locale) {
        state.locale = locale;
    },
    SET_LOGOUT_CONFIRMATION_MODAL(state, payload) {
        state.logoutConfirmationModal = payload;
    },
};

const actions = {
    async fetchAccessToken({ commit }) {
        if (isUsingKc) return;
        if (hasRtkpCookie()) {
            const response = await this._vm.$api.auth.getAccessToken();
            commit('SET_ACCESS_TOKEN', response.data.accessToken);
        }
    },

    async logout({ commit }) {
        try {
            commit('SET_LOADING', true);
            await this._vm.$api.auth.logoutJwt();
            commit('SET_ACCESS_TOKEN', null);
            commit('SET_LOGGED_USER', null);
            commit('SET_PATIENTS', []);
        }
        finally {
            commit('SET_LOADING', false);
        }
    },

    async getLoggedUser({ getters, commit }) {
        if (getters.getAccessToken) {
            const response = await this._vm.$api.auth.getMyPatient();
            commit('SET_LOGGED_USER', response.data);
        }
    },

    async getLoggedUserCommunication({ getters, commit }) {
        if (getters.getAccessToken) {
            const response = await this._vm.$api.auth.getMyPatientCommunication();
            commit('SET_LOGGED_USER_COMMUNICATION', response.data);
        }
    },

    async getPatients({ getters, commit }) {
        if (getters.getAccessToken) {
            const response = await this._vm.$api.auth.getAllPatients();
            commit('SET_PATIENTS', response.data);
        }
    },

    async openSignInWindow({ getters, dispatch, commit }, isRegiser = false) {
        const oAuthUrl = isRegiser
            ? '/oauth/register'
            : '/oauth/login';
        const authenticationRoute = `${getters.getProfileUrl}${oAuthUrl}?baseUrl=${window.location.origin}&locale=${getters.getLocale}`;
        try {
            await signInWindow(authenticationRoute);
            commit('SET_LOADING', true);
            await dispatch('fetchAccessToken');
            await Promise.all([
                dispatch('getLoggedUser'),
                dispatch('getLoggedUserCommunication'),
                dispatch('getPatients'),
            ]);
        }
        finally {
            commit('SET_LOADING', false);
        }
    },

    async openRegisterWindow({ getters }, payload) {
        const { clientDataForUrl } = payload;
        await signInWindow(urlFromClientData(getters.getProfileUrl, { ...clientDataForUrl, baseUrl: window.location.origin }));
    },
};

export const jwt = {
    namespaced: true,
    actions,
    getters,
    mutations,
    state,
};
